import React from 'react';
import BlogArticle from 'components/blog/blogArticle';
import { Post } from 'components/blog/content/styled';
import { authorKalina as author } from 'data/authors';
import img from 'img/blog/cover.png';

import img1 from 'img/blog/qualitative-vs-quantitative.png';
import img2 from 'img/blog/interview.png';
import img3 from 'img/blog/focus-group.png';
import img4 from 'img/blog/session-replay.png';
import img5 from 'img/blog/mouse-movement.png';
import img6 from 'img/blog/feedback-form.png';
import img7 from 'img/blog/think-aloud.png';
import img8 from 'img/blog/plan.jpg';
import img9 from 'img/blog/ls-website-sc.png';
import img10 from 'img/blog/loockback.png';
import img11 from 'img/blog/reframer.png';
import img12 from 'img/blog/testingtime.png';
import img13 from 'img/blog/table.svg';

const Content = () => {
  return (
    <Post>
      <p>
        Is it better to choose quality over quantity? Or is it about finding the right balance
        between both?
      </p>
      <p>
        In the case of UX research, it’s about finding the right match for your needs. First, it’s
        best to ask yourself what is the goal of your research. With the answer in mind, it will be
        much easier to pick the right method. And even if you choose to focus on numbers,
        qualitative data can help you get the full picture. It’s invaluable for both UX design teams
        and other project stakeholders.
      </p>
      <p>With this guide, you’re going to learn:</p>
      <ul>
        <li>
          what are the main differences between qualitative and quantitative methods in UX research
        </li>
        <li>when to use qualitative user research methods</li>
        <li>what are the most popular qualitative UX research methods</li>
        <li>what are some of the most useful qualitative UX research tools</li>
      </ul>
      <p>Ready to improve the quality of your research? Keep on reading to find out more!</p>
      <h2 id="table-of-contents">Table of contents</h2>
      <ol>
        <li>
          <p>
            <a href="#uxresearch">Qualitative vs. quantitative UX research</a>
          </p>
          <ul>
            <li>
              <a href="#diffrentstypes">Why you should combine differents types of user research</a>
            </li>
            <li>
              <a href="#qualitativeux">What is qualitative UX research</a>
            </li>
          </ul>
        </li>
        <li>
          <p>
            <a href="#uxresearchmethods">Qualitative UX research methods overview</a>
          </p>
          <ul>
            <li>
              <a href="#interviews">Interviews</a>
            </li>
            <li>
              <a href="#focusgroups">Focus groups</a>
            </li>
            <li>
              <a href="#sessionreplays">Session replays</a>
            </li>
            <li>
              <a href="#feedbackforms">Feedback forms</a>
            </li>
            <li>
              <a href="#protocol">Think-aloud protocol</a>
            </li>
            <li>
              <a href="#participatorydesign">Participatory design</a>
            </li>
          </ul>
        </li>
        <li>
          <p>
            <a href="#uxtoolbox">Qualitative UX researcher toolbox</a>
          </p>
          <ul>
            <li>
              <a href="#livesession">LiveSession</a>
            </li>
            <li>
              <a href="#lookback">Lookback</a>
            </li>
            <li>
              <a href="#reframer">Reframer</a>
            </li>
            <li>
              <a href="#testingtime">TestingTime</a>
            </li>
          </ul>
        </li>
        <li>
          <p>
            <a href="#keytakeaways">Key takeaways for your qualitative UX research strategy</a>
          </p>
        </li>
      </ol>
      <h2 id="uxresearch">Qualitative vs. quantitative UX research</h2>

      <p>
        <img src={img1} alt="Qualitative vs. quantative" title="UX research" />
      </p>
      <p>
        Being data-driven isn’t all about numbers. To gain an in-depth insight into the user
        behavior, you need to turn to qualitative data as well. Effective design research should
        cover both sides of the coin, qualitative and quantitative.
      </p>
      <h3 id="diffrentstypes">Why you should combine differents types of user research</h3>

      <p>
        <strong>Quantitative UX research</strong> (sometimes shortened to <em>quant</em> for
        clarity) will tell you <em>what</em> is happening. It gives you results that are objectively
        measurable and can be presented in a numerical form. Some of the most popular research
        techniques for this kind of user research include{' '}
        <a href="/blog/5-tips-for-better-a-b-tests/" target="_blank" rel="noopener noreferrer">
          A/B testing
        </a>
        , questionnaires and web analytics benchmarking.
      </p>
      <p>
        In other words, quantitative research leaves you with hard data. This kind of information is
        invaluable yet it requires <strong>more context</strong> to draw actionable insights.
      </p>
      <p>
        And here’s where <strong>qualitative UX research</strong> comes in useful.
      </p>
      <p>
        True, this kind of research won’t provide you with statistically significant <em>what</em>,
        but it will give you an equally important <em>why</em> instead.
      </p>
      <p>Not sure how that would work? Here’s a simple example:</p>
      <p>
        Imagine that you’re running an e-commerce store with low conversion rates. After analyzing
        your sales funnel, you find out that your users drop out when they need to choose the
        payment method. This is the quantitative part – you know what is the problem but you don’t
        know what caused it.
      </p>
      <p>
        Once you find out that the issue lies there, you take a closer look at session recordings
        from this part of the funnel. With this qualitative research method, you see that the users
        don’t realize that the “Order and pay” button is there – it’s on the website, but it’s not
        visible above the fold. Because of that, the visitor doesn’t know that they need to scroll
        down. Armed with this knowledge, you can easily solve the issue.
      </p>
      <p>
        See the pattern? Without this kind of user research, you wouldn’t have realized that the
        problem lies there.
      </p>
      <p>So, how do qualitative research techniques work?</p>
      <h3 id="qualitativeux">What is qualitative UX research</h3>

      <p>
        <strong>Qualitative methods</strong> in UX research (you can use the shorter <em>qual</em>{' '}
        form as well) are based on direct observation of user behavior. If your users are struggling
        with a particular element of the website, you will see exactly how they interact with it and
        what can be the cause of the problem.
      </p>
      <p>
        What’s more, qualitative methods can be used at any stage of the design process. In many
        cases, you don’t need to have a fully-functioning app to conduct user research. No need to
        rely on trial and error – getting feedback from your users at an early stage can help you
        build a better product without guessing.
      </p>
      <p>
        Another big advantage of qualitative research is that even if something is not clear at
        first sight, you can usually ask additional questions to get more insight. Again, numbers
        are a clear and objective source, but they’re pretty superficial. Digging a little deeper
        can lead to unexpected findings, especially early in the process.
      </p>
      <p>
        So, how do you get started with qualitative research? As you can probably imagine, the field
        is quite extensive and it’s virtually impossible to cover everything at once. To help you
        out, we’re describing the most prominent qualitative UX research methods below.
      </p>
      <h2 id="uxresearchmethods">Qualitative UX research methods overview</h2>

      <h3 id="interviews">Interviews</h3>

      <p>
        <img src={img2} alt="Research method" title="Interviews" />
      </p>
      <p>
        <strong>Individual in-depth interviews (IDI)</strong> are one of the most commonly used
        qualitative UX research methods. This method is popular mainly because it feels natural,
        kind of like having a casual conversation. What’s more, interviews can serve different
        purposes. They can be used during different stages of the design process; not only testing
        but also definition and ideation.
      </p>
      <p>
        IDIs can be divided into <strong>structured</strong> and <strong>unstructured</strong>{' '}
        interviews. Most of the time, the interview covers broad topics for discussion and sets a
        direction for the conversation. In this case, it falls in the middle of the spectrum and can
        be classified as <strong>semi-structured</strong>.
      </p>
      <p>
        Sounds good, but there’s got to be a catch, right? There is one important thing to keep in
        mind: <strong>interviews are a declarative method.</strong>The answers are not always
        directly reflected in reality and the user doesn’t even have to be aware of that
      </p>
      <p>
        It’s not about giving wrong answers deliberately. Rather than that, the user’s memory may be
        flawed or they might be subconsciously trying to show themselves in a better light. Because
        of that, in-depth interviews can be a valuable source yet the answers still need to be{' '}
        <strong>validated with behavioral methods</strong>.
      </p>
      <p>
        What’s more, <strong>interviews are time-consuming</strong>. Even if you’re conducting them
        remotely, you still need to spare some time for a 1-on-1 meeting. This leads to another
        common problem: when your target audience is hard to reach, it can be challenging to find a
        user that’s willing to devote some of their time. It’s a common practice to use incentives,
        such as discount codes or gift cards. These usually work well but also increase the cost of
        your research.
      </p>
      <p>
        Despite all these limitations, IDIs still remain one of the most popular (and truly
        valuable) qualitative UX research methods. During an individual in-depth interview, the
        user’s answers are not affected by the group’s influence. If something interesting comes up,
        the researcher can always ask additional questions. These advantages make it insightful and
        quite easy to conduct as well.
      </p>
      <h3 id="focusgroups">Focus groups</h3>

      <p>
        <img src={img3} alt="Focus groups" title="Groups" />
      </p>
      <p>
        <strong>Focus group interviews (FGIs)</strong> are moderated group interviews that first
        became popular in the field of marketing research. The session typically lasts for around 2
        hours and is based on a previously created scenario.
      </p>
      <p>
        As you can imagine, FGIs can be challenging to organize, as this method requires you to
        gather 4-8 people in one place at the same time. Partially because of that,{' '}
        <a
          href="https://www.userinterviews.com/ux-research-field-guide-chapter/focus-groups"
          target="_blank"
          rel="noopener noreferrer"
        >
          UX focus groups
        </a>{' '}
        are decreasing in popularity.
      </p>
      <p>
        There are other factors that shape this tendency, such as <strong>social influence</strong>.
        When you’ve got several participants in one room, it’s likely that one of them will dominate
        the discussion, even despite the moderator’s intervention. Also, focus groups are a poor
        choice for discussing delicate topics, as the participants need to share their opinions in
        public. And again, FGIs are a declarative method, just like interviews.
      </p>
      <p>
        So, what are the bright sides? Focus groups can be a quite efficient alternative to in-depth
        interviews if you can gather all your participants in one room at the same time. This method
        can also prove useful at the very start of the process, as the group might come up with some
        genuinely creative ideas.
      </p>
      <h3 id="sessionreplays">Session replays</h3>

      <p>
        <img src={img4} alt="Method - session replays" title="Session" />
      </p>
      <p>
        Even if you’re short on time and money,{' '}
        <a href="/blog/what-is-session-replay/" target="_blank" rel="noopener noreferrer">
          session replay
        </a>{' '}
        is an effective qualitative research method you can try right away. All you need to do is
        paste a small JavaScript snippet in your website’s code. Thanks to this little tweak, you’ll
        be able to see how the visitors interact with your product.
      </p>
      <p>
        <a href="/signup/" target="_blank" rel="noopener noreferrer" className="action-banner">
          Try session replay software for free and see how it works!
        </a>
      </p>
      <p>
        Watching{' '}
        <a href="/blog/analyze-session-recordings/" target="_blank" rel="noopener noreferrer">
          session recordings
        </a>{' '}
        is a lot like sitting next to your user. Session replay tools replicate all the mouse
        movements, clicks and other activities. With this solution, you see exactly what the user
        sees on the screen. For instance, if they encounter a bug, you’ll also see when they got
        lost and what went wrong.
      </p>
      <p>
        Although it might be surprising, in some cases UX designers may{' '}
        <strong>prefer session replays</strong> to real-time interviews. There are two main reasons
        behind that:
      </p>
      <ul>
        <li>
          First of all, <strong>session recordings are purely behavioral</strong>. The results are
          not affected by the user’s cognitive limitations or group influence. You see how the
          person interacts with your product – nothing less, nothing more.
        </li>
        <li>
          <strong>The user is not under pressure</strong>. They don’t feel like they’re being
          watched, and most of the time they don’t even realize that they’re being recorded. Because
          of that, they behave more naturally.
        </li>
      </ul>
      <p>
        And the best part? Session recording tools include many different tracking methods, starting
        from <strong>mouse movement tracking</strong>:
      </p>
      <p>
        <img src={img5} alt="Session replay - mouse movement" title="Livesession replay" />
      </p>
      <p>
        This method allows you to see how the user moves the mouse cursor. It’s a more affordable
        alternative to{' '}
        <a
          href="https://www.nngroup.com/reports/how-to-conduct-eyetracking-studies/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          eye-tracking
        </a>{' '}
        studies, which call for extensive budget and expensive gear. With mouse movement tracking,
        you can see how your users think and where they look for particular elements of the website.
      </p>
      <p>
        Another important feature that’s commonly used in session recordings is{' '}
        <a
          href="/blog/click-tracking-on-your-website-all-you-need-to-know/"
          target="_blank"
          rel="noopener noreferrer"
        >
          click tracking
        </a>
        . As the name suggests, it allows you to see where the users click on your website. And
        there is even more to that – many session replay tools, such as LiveSession, detect{' '}
        <a href="/blog/error-rage-clicks/" target="_blank" rel="noopener noreferrer">
          rage clicks and error clicks
        </a>{' '}
        as well. With this functionality, you’re able to spot points of frustration very easily.
      </p>
      <p>
        A huge advantage of session recordings is that you don’t need a fully-functioning website –
        an HTML prototype will do just fine. You can upload it to any server and show to
        participants recruited for your remote user research.
      </p>
      <p>
        Alright, so what are the downsides? First of all, this UX research method can’t be used in
        the very early stages. Even though you don’t need a released website, you still got to
        provide at least a prototype.
      </p>
      <p>
        The analysis can be challenging too, as the recordings don’t provide you with additional
        context. They’re a goldmine of information yet they can still be enriched with other sources
        of data, such as in-depth interviews mentioned before.
      </p>
      <p>
        <a href="/signup/" target="_blank" rel="noopener noreferrer" className="action-banner">
          Want to test session recordings on your website? Try LiveSession for free. No credit card
          required!
        </a>
      </p>
      <h3 id="feedbackforms">Feedback forms</h3>

      <p>
        <img src={img6} alt="Feedback form" title="Form" />
      </p>
      <p>
        <em>On a scale from 1 to 5, how satisfied are you with this feature?</em>
      </p>
      <p>
        This is a classic example of a quantitative feedback form.{' '}
        <strong>Qualitative feedback forms</strong>, on the other hand, are about asking the user to
        share their thoughts in a written form. Usually, they’re expected to write just one or two
        sentences.\ \ This kind of form can be used to ask if the user is satisfied with a new
        feature, or just to get general suggestions on product improvements. Once again, this is a
        declarative method, and the users are not always keen on answering additional questions.
        They’re focused on fulfilling their goal – why would they pay attention to surveys?
      </p>
      <p>
        Forms like these, however, have other significant advantages. They’re cheap and can be
        quickly implemented on the website. And the cherry on top: in this case, data analysis is
        pretty fast and you can easily draw actionable insights from the responses.
      </p>
      <h3 id="protocol">Think-aloud protocol</h3>

      <p>
        <img src={img7} alt="What people think?" title="Think-aloud" />
      </p>
      <p>
        <strong>Think-aloud protocol</strong> (also known as <em>thinking aloud protocol</em>)is one
        of the most popular methods in usability testing. The idea here is quite simple: you ask the
        user to speak all their thoughts out loud. They are supposed to say whatever comes to their
        mind.
      </p>
      <p>
        With this approach, the researcher can see how the user interprets different functionalities
        and how they perceive the website in general. In other words, it gives a solid background
        for understanding why the users behave in a certain way. Actually, it’s the closest you’ll
        ever get to reading your user’s mind, right?
      </p>
      <p>
        To top it all off, the think-aloud protocol is also cheap and easy to master. It meets the
        right objective behind usability testing: it allows you to see if your design is actually
        easy to use.
      </p>
      <p>
        So, what are the limitations in this case? First of all, this usability testing method also
        requires at least a prototype. It’s also good to keep in mind that this method is
        declarative, which is why the declared problems are not always reflected in reality. The
        situation is a bit unnatural too, which of course may influence the answers.
      </p>
      <h3 id="participatorydesign">Participatory design</h3>

      <p>
        The methods described before apply either to coming up with ideas or to testing a
        functioning product.
      </p>
      <p>
        And what if you could actually <em>involve</em> your users in the design process?
      </p>
      <p>
        <strong>Participatory design</strong> is based on that idea. It allows the user to step in
        the designer’s shoes – at least to some extent. They can take part in{' '}
        <strong>design workshops</strong>, such as the ones conducted with the{' '}
        <strong>design studio method</strong>:
      </p>
      <p>
        <img src={img8} alt="Plan of design" title="Plan" />
      </p>
      <p>
        <strong>
          <a
            href="https://www.nngroup.com/articles/facilitating-design-studio-workshop/"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            Design studio
          </a>
        </strong>{' '}
        is a simple tool that can be used to find your user’s pain points and generate innovative
        ideas. All you need is a moderator, an enthusiastic group, a couple of pens and sheets of
        paper.
      </p>
      <p>
        The obstacles? The main one probably already crossed your mind:{' '}
        <strong>participatory design is expensive</strong>.
      </p>
      <p>
        Yes, it’s a source of valuable qualitative insights, but it also adds additional steps to
        the design process.
      </p>
      <p>
        Not every team can afford this, especially with limited resources. On the other hand, it
        might help you avoid pitfalls during the redesign process. Participatory design is a great
        way to make your users feel included and validate your first ideas.
      </p>
      <p>
        Now that you know some of the most popular UX research methods, it’s time to move on to the
        next step – the necessary tools.
      </p>
      <h2 id="uxtoolbox">Qualitative UX researcher toolbox</h2>

      <p>
        Empathy, inquisitiveness and brilliant communication skills are must-have traits for a good
        qualitative UX researcher. Personal qualities are crucial, but they’re not enough: even the
        best professional won’t get far without the right tools.
      </p>
      <p>Here are some of the tools that are essential in every qualitative UX research toolbox:</p>
      <h3 id="livesession">LiveSession</h3>

      <p>
        <img src={img9} alt="LiveSession as a UX research toolbox" title="LiveSession website" />
      </p>
      <p>
        If you need an advanced session recording tool, <strong>LiveSession</strong> is what you’re
        looking for. The app records user activities (scrolls, clicks, mouse movements) and offers
        advanced segmentation to analyze them later. You can filter the recordings according to a
        wide choice of different variables. This will help you get valuable insights on user
        behavior.
      </p>
      <h3 id="lookback">Lookback</h3>

      <p>
        <img src={img10} alt="Lookback as a UX research toolbox" title="Lookback website" />
      </p>
      <p>
        <a href="https://lookback.io/" target="_blank" rel="noopener noreferrer nofollow">
          Lookback
        </a>{' '}
        is a user recording tool that allows you to conduct interviews online. It can be used for
        both in-house and remote interviews. Lookback not only captures the screen, but also records
        the user’s face and voice. You can see and hear their reactions, which gives you plenty of
        valuable additional information. It’s a great tool to use for think-aloud protocol usability
        testing and interviews as well.
      </p>
      <h3 id="reframer">Reframer</h3>

      <p>
        <img src={img11} alt="Reframer as a UX research toolbox" title="Reframer website" />
      </p>
      <p>
        Qualitative research comes with <em>a lot</em> of note-taking.{' '}
        <a
          href="https://www.optimalworkshop.com/reframer"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Reframer
        </a>{' '}
        is a tool to make this part of the process more effective. It allows you to set up the
        objectives of your research first, take notes and then analyze the data in a more efficient
        way. The app also makes it easier to look for certain topics in your notes later – and we
        all know how messy it can get, right?
      </p>
      <h3 id="testingtime">TestingTime</h3>

      <p>
        <img src={img12} alt="TestingTime as a UX research toolbox" title="TestingTime website" />
      </p>
      <p>
        Having trouble recruiting research participants? There are tools to help with that. For
        instance,{' '}
        <a
          href="https://www.testingtime.com/en/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          TestingTime
        </a>{' '}
        can help you find users for different kinds of studies, from focus groups and individual
        interviews to workshops and surveys. This platform has a quite extensive base of
        participants and if you can’t find people to take part in your research, this might be the
        solution you’re after.
      </p>
      <h2 id="keytakeaways">Key takeaways for your qualitative UX research strategy</h2>

      <p>
        We’re aware that the qualitative research field may seem overwhelming at first, even for
        experienced UX professionals. No matter how advanced you are, we would like to encourage you
        to stay curious, empathetic and open for new insights. Qualitative methods can lead to
        surprising findings that may even drive your design strategy to a whole new direction. We
        hope that you could learn something new with this article and that you’ve found some
        inspiration for your future research.
      </p>
      <p>
        <img src={img13} alt="Qualitative vs. Quantitative" title="Table" />
      </p>
    </Post>
  );
};

const Wrapped = BlogArticle(Content);

export const frontmatter = {
  title: 'Guide to Qualitative UX Research ',
  url: '/blog/guide-to-qualitative-ux-research/',
  description: `Is it better to choose quality over quantity? Or is it about finding the right
  balance between both?`,
  author,
  img,
  imgSocial: img,
  date: '2020-02-04',
  category: '',
  group: 'Research',
  timeToRead: 12,
};

export default () => (
  <Wrapped
    title={frontmatter.title}
    category={frontmatter.category}
    description={frontmatter.description}
    date={frontmatter.date}
    author={frontmatter.author}
    url={frontmatter.url}
    img={frontmatter.img}
    imgSocial={process.env.GATSBY_HOST + frontmatter.img}
    group={frontmatter.group}
    timeToRead={frontmatter.timeToRead}
  />
);
